import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { simulacionesGpsService } from '../services/simulacionesGpsService'
import { toast } from 'react-hot-toast'
import { formatearFechaParaInput, obtenerFechaActual } from '../utils/dateUtils'
import { 
  ArrowLeft, 
  Save, 
  X, 
  MapPin,
  Clock,
  Calendar,
  Smartphone,
  Car,
  Route,
  Settings,
  Copy
} from 'lucide-react'

const SimulacionGPSForm = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  // Declarar urlParams y copiarId al inicio
  const urlParams = new URLSearchParams(location.search)
  const copiarId = urlParams.get('copiar')
  const isCopiando = !id && copiarId
  const [loading, setLoading] = useState(false)
  const [simulacion, setSimulacion] = useState({
    fechaLanzamiento: '',
    imei: '',
    imei_real: '',
    placa: '',
    fechaHoraInicial: '',
    fechaHoraFinal: '',
    puntos: '',
    estado: '0',
    estado_replica: '0',
    tipoSimulacion: 'REALTIME',
    nombre_ruta: '',
    // Nuevos campos de plataforma con valores por defecto
    tracker_url_origen: 'https://gps.tecnytte.com',
    tracker_url_destino: 'https://gps.tecnytte.com',
    api_key_origen: '4C992B2947DFF375E4D75337E9EB5AB1',
    api_key_destino: '4C992B2947DFF375E4D75337E9EB5AB1',
    user_token_origen: '29A51402D29359061D885496BBFD5CC7',
    user_token_destino: '29A51402D29359061D885496BBFD5CC7'
  })

  // Cargar simulación si es edición o copia
  useEffect(() => {
    if (id) {
      cargarSimulacion()
    } else if (copiarId) {
      cargarSimulacionParaCopiar(copiarId)
    } else {
      // Verificar si hay datos guardados en localStorage para duplicar
      const datosReplicaDuplicada = localStorage.getItem('datosReplicaDuplicada')
      if (datosReplicaDuplicada) {
        try {
          const datos = JSON.parse(datosReplicaDuplicada)

          setSimulacion({
            ...datos,
            fechaLanzamiento: formatearFechaParaInput(datos.fechaLanzamiento),
            fechaHoraInicial: formatearFechaParaInput(datos.fechaHoraInicial),
            fechaHoraFinal: formatearFechaParaInput(datos.fechaHoraFinal)
          })
          
          // Limpiar localStorage después de usar los datos
          localStorage.removeItem('datosReplicaDuplicada')
          
          toast.success('Datos de réplica cargados. Puedes modificar los campos necesarios.')
        } catch (error) {
          console.error('Error cargando datos de réplica:', error)
          localStorage.removeItem('datosReplicaDuplicada')
          // Continuar con fechas por defecto
          const fechaActual = obtenerFechaActual()
          setSimulacion(prev => ({
            ...prev,
            fechaLanzamiento: fechaActual,
            fechaHoraInicial: fechaActual,
            fechaHoraFinal: fechaActual
          }))
        }
      } else {
        // Establecer fechas por defecto para nueva simulación
        const fechaActual = obtenerFechaActual()
        setSimulacion(prev => ({
          ...prev,
          fechaLanzamiento: fechaActual,
          fechaHoraInicial: fechaActual,
          fechaHoraFinal: fechaActual
        }))
      }
    }
  }, [id, location.search])

  const cargarSimulacion = async () => {
    try {
      setLoading(true)
      const response = await simulacionesGpsService.getSimulacion(id)
      const data = response.data

      setSimulacion({
        ...data,
        fechaLanzamiento: formatearFechaParaInput(data.fechaLanzamiento),
        fechaHoraInicial: formatearFechaParaInput(data.fechaHoraInicial),
        fechaHoraFinal: formatearFechaParaInput(data.fechaHoraFinal)
      })
    } catch (error) {
      console.error('Error cargando simulación:', error)
      toast.error('Error al cargar la simulación')
      navigate('/simulaciones-gps')
    } finally {
      setLoading(false)
    }
  }

  const cargarSimulacionParaCopiar = async (copiarId) => {
    try {
      setLoading(true)
      const response = await simulacionesGpsService.getSimulacion(copiarId)
      const data = response.data

      // Copiar datos pero modificar el nombre para indicar que es una copia
      setSimulacion({
        ...data,
        nombre_ruta: `${data.nombre_ruta} (Copia)`,
        fechaLanzamiento: formatearFechaParaInput(data.fechaLanzamiento),
        fechaHoraInicial: formatearFechaParaInput(data.fechaHoraInicial),
        fechaHoraFinal: formatearFechaParaInput(data.fechaHoraFinal)
      })
      
      toast.success('Datos copiados correctamente. Puedes modificar los campos necesarios.')
    } catch (error) {
      console.error('Error cargando simulación para copiar:', error)
      toast.error('Error al cargar los datos para copiar')
      navigate('/simulaciones-gps')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    try {
      setLoading(true)

      // Validaciones
      if (!simulacion.nombre_ruta.trim()) {
        toast.error('El nombre de la ruta es requerido')
        return
      }

      if (!simulacion.imei.trim()) {
        toast.error('El IMEI simulado es requerido')
        return
      }

      if (!simulacion.imei_real.trim()) {
        toast.error('El IMEI real es requerido')
        return
      }

      if (!simulacion.placa.trim()) {
        toast.error('La placa es requerida')
        return
      }

      if (!simulacion.puntos.trim()) {
        toast.error('Los puntos de la ruta son requeridos')
        return
      }

      // Validar formato JSON de puntos
      try {
        JSON.parse(simulacion.puntos)
      } catch (error) {
        toast.error('Los puntos deben tener un formato JSON válido')
        return
      }

      if (id) {
        // Actualizar simulación existente
        await simulacionesGpsService.updateSimulacion(id, simulacion)
        toast.success('Simulación actualizada exitosamente')
      } else {
        // Crear nueva simulación
        await simulacionesGpsService.createSimulacion(simulacion)
        toast.success('Simulación creada exitosamente')
      }

      navigate('/simulaciones-gps')
    } catch (error) {
      console.error('Error guardando simulación:', error)
      toast.error('Error al guardar la simulación')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setSimulacion(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const generarPuntosEjemplo = () => {
    const ejemplo = {
      plc: "0.1v",
      markers: [],
      routes: [
        {
          name: "UNACEM-TALLERSANTAANITA-PRADO",
          color: "#FF0000",
          visible: "true",
          name_visible: "true",
          deviation: "0.5",
          points: "-12.182709,-76.910103,-12.173494,-76.916067,-12.171574,-76.916968,-12.169053,-76.918243,-12.167749,-76.919574,-12.167452,-76.920649,-12.167712,-76.92272,-12.167128,-76.924917,-12.168698,-76.927532,-12.171646,-76.931554,-12.174036,-76.934812,-12.177437,-76.939481,-12.176919,-76.939874,-12.176128,-76.941313,-12.175111,-76.942474,-12.174092,-76.943299,-12.172459,-76.94462,-12.170761,-76.946016,-12.169187,-76.947417,-12.168286,-76.948213,-12.167002,-76.949322,-12.166029,-76.950234,-12.164482,-76.951717,-12.162957,-76.952987,-12.161358,-76.954364,-12.160725,-76.954894,-12.159893,-76.955629,-12.158414,-76.957385,-12.158005,-76.95789,-12.156786,-76.959361,-12.155777,-76.96056,-12.155098,-76.961402,-12.154748,-76.961844,-12.15336,-76.963536,-12.151617,-76.965664,-12.150116,-76.967396,-12.148105,-76.969263,-12.147155,-76.970107,-12.146369,-76.970829,-12.144292,-76.972725,-12.14281,-76.974049,-12.141738,-76.974554,-12.140808,-76.974923,-12.139271,-76.974792,-12.13828,-76.974943,-12.136949,-76.975591,-12.133193,-76.976603,-12.129984,-76.976705,-12.129233,-76.976669,-12.127132,-76.976683,-12.123105,-76.976986,-12.1172,-76.977218,-12.112592,-76.977851,-12.105656,-76.978776,-12.099556,-76.97963,-12.093907,-76.980403,-12.089674,-76.980919,-12.087953,-76.98121,-12.086553,-76.981957,-12.08587,-76.982177,-12.085384,-76.98217,-12.084791,-76.981928,-12.082856,-76.979949,-12.080704,-76.977669,-12.079581,-76.976762,-12.078431,-76.976082,-12.075303,-76.974731,-12.070719,-76.972803,-12.06801,-76.971672,-12.066956,-76.971486,-12.065056,-76.971196,-12.06357,-76.971309,-12.06188,-76.971586,-12.060553,-76.972051,-12.058741,-76.972885,-12.058526,-76.972871,-12.058221,-76.972611,-12.057468,-76.97088,-12.056676,-76.969059,-12.056364,-76.968341,-12.054923,-76.964952,-12.054749,-76.964541,-12.053369,-76.961363,-12.052993,-76.960499,-12.051167,-76.956258,-12.050597,-76.954833,-12.049988,-76.953276,-12.049285,-76.951545,-12.048658,-76.949927,-12.04591,-76.950549,-12.04326,-76.951604,-12.041159,-76.952486,-12.037834,-76.953969,-12.03613,-76.949705,-12.035677,-76.949003,-12.034978,-76.949257,-12.032961,-76.949217,-12.03177,-76.945752,-12.030793,-76.943432,-12.027934,-76.944158"
        }
      ],
      zones: []
    }
    setSimulacion(prev => ({
      ...prev,
      puntos: JSON.stringify(ejemplo, null, 2)
    }))
    toast.success('Ejemplo de ruta avanzada generado')
  }

  if (loading && (id || copiarId)) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  // Determinar el tipo de formulario
  // const urlParams = new URLSearchParams(location.search)
  // const copiarId = urlParams.get('copiar')
  // const isCopiando = !id && copiarId

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            {id ? 'Editar Simulación GPS' : isCopiando ? 'Copiar Simulación GPS' : 'Nueva Simulación GPS'}
            {isCopiando && <Copy className="w-6 h-6 text-blue-600" />}
          </h1>
          <p className="text-gray-600">
            {id ? 'Modifica los datos de la simulación' : 
             isCopiando ? 'Crea una nueva simulación basada en una existente' : 
             'Crea una nueva simulación GPS'}
          </p>
        </div>
        <button
          onClick={() => navigate('/simulaciones-gps')}
          className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <ArrowLeft className="w-5 h-5" />
          Volver
        </button>
      </div>

      {/* Formulario */}
      <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Fecha de Lanzamiento */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Fecha de Lanzamiento
              </label>
              <input
                type="datetime-local"
                name="fechaLanzamiento"
                value={simulacion.fechaLanzamiento}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Momento en que el servicio automático dispara la ruta (PASTTIME/REALTIME). Debe coincidir minuto a minuto.</p>
            </div>

            {/* IMEI Simulado */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Smartphone className="w-4 h-4 inline mr-1" />
                IMEI Simulado
                <span className="ml-1 text-gray-400" title="Número IMEI del dispositivo GPS simulado">
                  ⓘ
                </span>
              </label>
              <input
                type="text"
                name="imei"
                value={simulacion.imei}
                onChange={handleChange}
                placeholder="123456789012345"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* IMEI Real */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Smartphone className="w-4 h-4 inline mr-1" />
                IMEI Real
                <span className="ml-1 text-gray-400" title="Número IMEI del dispositivo GPS real (opcional)">
                  ⓘ
                </span>
              </label>
              <input
                type="text"
                name="imei_real"
                value={simulacion.imei_real}
                onChange={handleChange}
                placeholder="123456789012345"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* Placa */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Car className="w-4 h-4 inline mr-1" />
                Placa
              </label>
              <input
                type="text"
                name="placa"
                value={simulacion.placa}
                onChange={handleChange}
                placeholder="ABC-123"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* Fecha y Hora Inicial */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Clock className="w-4 h-4 inline mr-1" />
                Fecha y Hora Inicial
              </label>
              <input
                type="datetime-local"
                name="fechaHoraInicial"
                value={simulacion.fechaHoraInicial}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* Fecha y Hora Final */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Clock className="w-4 h-4 inline mr-1" />
                Fecha y Hora Final
              </label>
              <input
                type="datetime-local"
                name="fechaHoraFinal"
                value={simulacion.fechaHoraFinal}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* Estado */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Settings className="w-4 h-4 inline mr-1" />
                Estado
                <span className="ml-1 text-gray-400" title="Estado de la simulación: Activo = Ejecutándose, Inactivo = Pausada">
                  ⓘ
                </span>
              </label>
              <select
                name="estado"
                value={simulacion.estado}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="0">Inactivo</option>
                <option value="1">Activo</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Activo (1) = el servicio toma esta autoruta. Inactivo = se ignora.</p>
            </div>

            {/* Estado Réplica */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Settings className="w-4 h-4 inline mr-1" />
                Estado Réplica
                <span className="ml-1 text-gray-400" title="Estado de la réplica: Activo = Réplica funcionando, Inactivo = Réplica pausada">
                  ⓘ
                </span>
              </label>
              <select
                name="estado_replica"
                value={simulacion.estado_replica}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="0">Inactivo</option>
                <option value="1">Activo</option>
              </select>
            </div>

            {/* Tipo de Simulación */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Route className="w-4 h-4 inline mr-1" />
                Tipo de Simulación
                <span className="ml-1 text-gray-400" title="Tipo de simulación: REALTIME = Tiempo real, PASTTIME = Tiempo pasado, REPLICA = Réplica">
                  ⓘ
                </span>
              </label>
              <select
                name="tipoSimulacion"
                value={simulacion.tipoSimulacion}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="REALTIME">REALTIME</option>
                <option value="PASTTIME">PASTTIME</option>
                <option value="REPLICA">REPLICA</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">REALTIME envía puntos en tiempo real. PASTTIME envía histórico más rápido. REPLICA copia el IMEI real al simulado (usa Estado Réplica, no Estado).</p>
            </div>

            {/* Nombre de Ruta */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-1" />
                Nombre de Ruta
                <span className="ml-1 text-gray-400" title="Nombre descriptivo de la ruta GPS (ej: Ruta Lima-Callao)">
                  ⓘ
                </span>
              </label>
              <input
                type="text"
                name="nombre_ruta"
                value={simulacion.nombre_ruta}
                onChange={handleChange}
                placeholder="Ruta Lima-Callao"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* Puntos de Ruta */}
            <div className="md:col-span-2 lg:col-span-3">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  <Route className="w-4 h-4 inline mr-1" />
                  Puntos de Ruta (JSON)
                  <span className="ml-1 text-gray-400" title="Coordenadas GPS de la ruta en formato JSON. Usa 'Generar ejemplo' para crear una ruta de prueba">
                    ⓘ
                  </span>
                </label>
                <button
                  type="button"
                  onClick={generarPuntosEjemplo}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Generar ejemplo
                </button>
              </div>
              <textarea
                name="puntos"
                value={simulacion.puntos}
                onChange={handleChange}
                rows="8"
                placeholder='[{"latitud": -12.345678, "longitud": -78.901234, "velocidad": 45, "evento": "normal"}]'
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Formato JSON con array de objetos con latitud, longitud, velocidad y evento
              </p>
            </div>
          </div>

          {/* Sección de Configuración de Plataformas */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-600" />
              Configuración de Plataformas GPS
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Configura las plataformas de origen y destino para la réplica de datos GPS
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Plataforma de Origen */}
              <div className="border border-gray-200 rounded-lg p-4 bg-blue-50">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-blue-600" />
                  Plataforma de Origen
                </h4>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      URL Tracker Origen
                    </label>
                    <input
                      type="url"
                      name="tracker_url_origen"
                      value={simulacion.tracker_url_origen}
                      onChange={handleChange}
                      placeholder="https://gps.tecnytte.com"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      API Key Origen
                    </label>
                    <input
                      type="text"
                      name="api_key_origen"
                      value={simulacion.api_key_origen}
                      onChange={handleChange}
                      placeholder="4C992B2947DFF375E4D75337E9EB5AB1"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-mono text-sm"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      User Token Origen
                    </label>
                    <input
                      type="text"
                      name="user_token_origen"
                      value={simulacion.user_token_origen}
                      onChange={handleChange}
                      placeholder="29A51402D29359061D885496BBFD5CC7"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-mono text-sm"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Plataforma de Destino */}
              <div className="border border-gray-200 rounded-lg p-4 bg-green-50">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-green-600" />
                  Plataforma de Destino
                </h4>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      URL Tracker Destino
                    </label>
                    <input
                      type="url"
                      name="tracker_url_destino"
                      value={simulacion.tracker_url_destino}
                      onChange={handleChange}
                      placeholder="https://gps.tecnytte.com"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      API Key Destino
                    </label>
                    <input
                      type="text"
                      name="api_key_destino"
                      value={simulacion.api_key_destino}
                      onChange={handleChange}
                      placeholder="4C992B2947DFF375E4D75337E9EB5AB1"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 bg-white font-mono text-sm"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      User Token Destino
                    </label>
                    <input
                      type="text"
                      name="user_token_destino"
                      value={simulacion.user_token_destino}
                      onChange={handleChange}
                      placeholder="29A51402D29359061D885496BBFD5CC7"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 bg-white font-mono text-sm"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Botones */}
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/simulaciones-gps')}
              className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg flex items-center gap-2"
            >
              <X className="w-5 h-5" />
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg flex items-center gap-2"
            >
              <Save className="w-5 h-5" />
              {loading ? 'Guardando...' : (id ? 'Actualizar' : 'Guardar')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default SimulacionGPSForm 